import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatIST } from '@/lib/utils';
import { formatApiError } from '@/lib/validators';
import api from '@/lib/api';
import {
  Plus, Search, RefreshCw, PlayCircle, MapPin, Archive, Undo2, Pencil, Boxes,
} from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton, SidePane } from '@/components/data';
import ViewSwitch from '@/pages/Tooling/ViewSwitch';

/**
 * The tooling the factory owns, as against the tooling it is waiting for.
 *
 * A row here is one physical piece — its code, the customer and job it was cut
 * for, the rack it lives on, how many times it has run. Not stock: the number
 * that moves is `times_used`, and a worn-out piece is retired rather than
 * counted down to zero.
 *
 * One screen, configured by a `tooling` from `toolings.js`, over two separate
 * registers — the same shape as the board beside it.
 */

const CONDITIONS = [
  ['good', 'Good'],
  ['worn', 'Worn'],
  ['damaged', 'Damaged'],
];

const CONDITION_TONE = {
  good: 'text-emerald-700',
  worn: 'text-amber-700',
  damaged: 'text-red-600',
};

const STATUS_TABS = [
  { key: 'active', label: 'On the rack', tone: 'good' },
  { key: 'retired', label: 'Retired' },
  { key: 'all', label: 'All' },
];

const EMPTY = {
  code: '', customer_id: '', customer_name: '', product_type: '',
  item_name: '', size: '', location: '', condition: 'good', notes: '',
};

export default function RegisterScreen({ tooling }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [status, setStatus] = useState('active');
  const [customerId, setCustomerId] = useState('');
  const [condition, setCondition] = useState('');
  const [size, setSize] = useState('');
  const [sizeDebounce, setSizeDebounce] = useState('');

  const [customers, setCustomers] = useState([]);
  const [pane, setPane] = useState(null); // 'add' | row
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [moving, setMoving] = useState(null);
  const [moveTo, setMoveTo] = useState('');
  const [retiring, setRetiring] = useState(null);
  const [retireReason, setRetireReason] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => setSizeDebounce(size), 400);
    return () => clearTimeout(timer);
  }, [size]);

  useEffect(() => { setPage(1); }, [status, customerId, condition, sizeDebounce, searchDebounce]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, status };
      if (searchDebounce) params.search = searchDebounce;
      if (customerId) params.customer_id = customerId;
      if (condition) params.condition = condition;
      if (sizeDebounce) params.size = sizeDebounce;
      const res = await api.get(tooling.registerApi, { params });
      setRows(res.data[tooling.plural] || []);
      setCounts(res.data.counts || {});
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e, `Failed to load the ${tooling.kind} register`));
    }
    setLoading(false);
  }, [page, status, customerId, condition, sizeDebounce, searchDebounce, tooling]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    api.get('/customers', { params: { limit: 200 } })
      .then(res => setCustomers(res.data.customers || []))
      .catch(() => setCustomers([]));
  }, []);

  const openAdd = () => { setForm(EMPTY); setPane('add'); };
  const openEdit = (row) => {
    setForm({
      code: row.code || '',
      customer_id: row.customer_id || '',
      customer_name: row.customer_name || '',
      product_type: row.product_type || '',
      item_name: row.item_name || '',
      size: row.size || '',
      location: row.location || '',
      condition: row.condition || 'good',
      notes: row.notes || '',
    });
    setPane(row);
  };

  const closePane = () => { setPane(null); setForm(EMPTY); };

  const save = async () => {
    setSubmitting(true);
    try {
      if (pane === 'add') {
        await api.post(tooling.registerApi, form);
        toast.success(`${tooling.label} added to the register`);
      } else {
        await api.put(`${tooling.registerApi}/${pane.id}`, form);
        toast.success(`${tooling.label} ${pane.code} updated`);
      }
      closePane();
      fetchRows();
    } catch (e) {
      toast.error(formatApiError(e, `Failed to save the ${tooling.kind}`));
    }
    setSubmitting(false);
  };

  const act = useCallback(async (row, path, body, done) => {
    try {
      await api.post(`${tooling.registerApi}/${row.id}/${path}`, body);
      toast.success(done);
      fetchRows();
    } catch (e) {
      toast.error(formatApiError(e, `Failed to update ${row.code}`));
    }
  }, [fetchRows, tooling]);

  const move = async () => {
    if (!moveTo.trim()) { toast.error('Give the rack it has moved to'); return; }
    const row = moving;
    setMoving(null);
    await act(row, 'move', { location: moveTo.trim() }, `${row.code} is now on ${moveTo.trim()}`);
    setMoveTo('');
  };

  const retire = async () => {
    const row = retiring;
    setRetiring(null);
    await act(row, 'retire', { reason: retireReason }, `${row.code} retired`);
    setRetireReason('');
  };

  const pickCustomer = (id) => setForm(f => ({
    ...f,
    customer_id: id,
    customer_name: customers.find(c => c.id === id)?.customer_name || '',
  }));

  const columns = useMemo(() => ([
    {
      key: 'code',
      header: 'Code',
      width: '7.5rem',
      render: (row) => (
        <span className={`block truncate font-semibold ${row.retired ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
          {row.code}
        </span>
      ),
    },
    {
      key: 'item_name',
      header: 'Made for',
      width: 'minmax(11rem,1.3fr)',
      render: (row) => (
        <>
          <span className="block truncate text-slate-700">{row.item_name || '—'}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">
            {row.origin_order_id || 'raised by hand'}
          </span>
        </>
      ),
    },
    {
      key: 'customer_name',
      header: 'Customer',
      width: 'minmax(9rem,1fr)',
      render: (row) => <span className="block truncate text-slate-700">{row.customer_name || '—'}</span>,
    },
    {
      key: 'product_type',
      header: 'Type',
      width: '7rem',
      mobile: false,
      render: (row) => <span className="block truncate text-[11px] text-slate-600">{row.product_type || '—'}</span>,
    },
    {
      key: 'size',
      header: 'Size',
      width: '7rem',
      // searchable, so it stays on the phone: hunting a size and not being shown
      // it is the one column a narrow screen cannot afford to drop
      render: (row) => <span className="block truncate text-[11px] text-slate-600">{row.size || '—'}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      width: '7rem',
      render: (row) => (
        <span className={`block truncate text-[12px] ${row.location ? 'text-slate-700' : 'text-slate-400'}`}>
          {row.location || 'not placed'}
        </span>
      ),
    },
    {
      key: 'condition',
      header: 'Condition',
      width: '6rem',
      mobile: false,
      render: (row) => (
        <span className={`text-[12px] font-medium ${CONDITION_TONE[row.condition] || 'text-slate-500'}`}>
          {row.condition || 'good'}
        </span>
      ),
    },
    {
      key: 'times_used',
      header: 'Used',
      align: 'right',
      width: '4.5rem',
      render: (row) => <span className="font-medium">{row.times_used || 0}</span>,
      total: (list) => list.reduce((s, r) => s + (r.times_used || 0), 0),
    },
    {
      key: 'last_used_at',
      header: 'Last used',
      width: '9rem',
      mobile: false,
      render: (row) => row.last_used_at ? (
        <span className="block truncate text-[11px] text-slate-600"
          title={row.last_used_order_id || undefined}>
          {formatIST(row.last_used_at, { compact: true })}
        </span>
      ) : <span className="text-[11px] text-slate-400">never</span>,
    },
    {
      key: 'retired',
      header: 'Status',
      width: '6.5rem',
      render: (row) => row.retired ? (
        <span className="text-[12px] font-medium text-slate-500"
          title={row.retired_reason || undefined}>Retired</span>
      ) : <span className="text-[12px] font-medium text-emerald-600">On the rack</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '8rem',
      align: 'right',
      render: (row) => (
        <span className="flex items-center justify-end gap-0.5">
          {!row.retired && (
            <>
              <CellButton icon={PlayCircle} label={`Mark ${row.code} used`} iconOnly
                onClick={() => act(row, 'mark-used', {}, `${row.code} used`)} />
              <CellButton icon={MapPin} label={`Move ${row.code}`} iconOnly
                onClick={() => { setMoving(row); setMoveTo(row.location || ''); }} />
            </>
          )}
          <CellButton icon={Pencil} label={`Edit ${row.code}`} iconOnly onClick={() => openEdit(row)} />
          {row.retired ? (
            <CellButton icon={Undo2} label={`Bring ${row.code} back`} iconOnly tone="good"
              onClick={() => act(row, 'unretire', {}, `${row.code} is back on the rack`)} />
          ) : (
            <CellButton icon={Archive} label={`Retire ${row.code}`} iconOnly tone="danger"
              onClick={() => { setRetiring(row); setRetireReason(''); }} />
          )}
        </span>
      ),
    },
  ]), [act]);

  const totalPages = Math.ceil(total / 50);

  return (
    <Screen data-testid={`${tooling.kind}-register-page`}>
      <CommandBar
        title={tooling.registerTitle}
        subtitle={`${total} ${total === 1 ? tooling.label.toLowerCase() : tooling.plural}`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchRows, disabled: loading },
          { key: 'add', label: `Add ${tooling.label.toLowerCase()}`, icon: Plus, tone: 'accent', onClick: openAdd },
        ]}
      >
        <ViewSwitch tooling={tooling} current="register" />
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-9 pl-8 text-sm sm:h-7 sm:text-xs"
            placeholder="Code, item, customer, rack, size…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={STATUS_TABS.map(t => ({
          key: t.key,
          label: t.label,
          value: counts[t.key] ?? 0,
          tone: t.tone,
        }))}
        activeKey={status}
        onSelect={(key) => setStatus(key || 'active')}
      >
        <select
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          aria-label="Filter by customer"
          className="h-9 max-w-[10rem] rounded border border-slate-200 bg-white px-2 text-sm text-slate-700 sm:h-7 sm:text-xs"
        >
          <option value="">All customers</option>
          {customers.map(c => <option key={c.id} value={c.id}>{c.customer_name}</option>)}
        </select>
        <select
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
          aria-label="Filter by condition"
          className="h-9 rounded border border-slate-200 bg-white px-2 text-sm text-slate-700 sm:h-7 sm:text-xs"
        >
          <option value="">Any condition</option>
          {CONDITIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        {/* size gets its own box beside the filters as well as being in the
            search net: "have we got one this size" is asked of the rack far
            more often than of any one customer */}
        <Input
          value={size}
          onChange={(e) => setSize(e.target.value)}
          aria-label="Filter by size"
          data-testid={`${tooling.kind}-size-filter`}
          placeholder="Any size"
          className="h-9 w-28 text-sm sm:h-7 sm:w-24 sm:text-xs"
        />
        {counts.damaged > 0 && (
          <span className="flex items-center gap-1 rounded bg-red-50 px-2 py-1 text-[11px] font-medium text-red-600">
            {counts.damaged} damaged
          </span>
        )}
      </StatStrip>

      <Pane
        title={`${tooling.label} register`}
        aside={`${rows.length} shown`}
        scroll={false}
        bodyClassName="flex flex-col"
        actions={totalPages > 1 && (
          <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
            {page} / {totalPages}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
          </span>
        )}
      >
        <DataGrid
          data-testid={`${tooling.kind}-register-table`}
          columns={columns}
          rows={rows}
          loading={loading}
          getRowId={(row) => row.id}
          onRowClick={openEdit}
          selectedId={pane && pane !== 'add' ? pane.id : null}
          showTotals={rows.length > 0}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Boxes className="h-8 w-8 text-slate-300" />
              {tooling.emptyRegister}
            </span>
          }
        />
      </Pane>

      <SidePane
        open={!!pane}
        onClose={closePane}
        title={pane === 'add' ? `Add ${tooling.label.toLowerCase()}` : `${tooling.label} ${pane?.code || ''}`}
        subtitle={pane === 'add'
          ? 'A piece the factory already has on a rack — leave the code blank for the next one in the sequence'
          : pane?.origin_order_id || undefined}
        width="26rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={closePane}>Cancel</Button>
            <Button size="sm" disabled={submitting} onClick={save}
              className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              {submitting ? 'Saving…' : pane === 'add' ? `Add ${tooling.label.toLowerCase()}` : 'Save changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-2.5 p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Code</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"
              placeholder={pane === 'add' ? 'Left blank, the register numbers it' : undefined}
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))} />
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Customer</span>
            <select
              value={form.customer_id}
              onChange={e => pickCustomer(e.target.value)}
              className="mt-0.5 h-10 w-full rounded border border-slate-200 bg-white px-2 text-base text-slate-700 sm:h-8 sm:text-sm"
            >
              <option value="">Not tied to a customer</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.customer_name}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Made for</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="Item or job it was cut for"
              value={form.item_name}
              onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Product type</span>
              <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm"
                value={form.product_type}
                onChange={e => setForm(f => ({ ...f, product_type: e.target.value }))} />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Size</span>
              <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="e.g. 20 x 30 in"
                value={form.size}
                onChange={e => setForm(f => ({ ...f, size: e.target.value }))} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Location</span>
              <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="Rack or shelf"
                value={form.location}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-slate-600">Condition</span>
              <select
                value={form.condition}
                onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}
                className="mt-0.5 h-10 w-full rounded border border-slate-200 bg-white px-2 text-base text-slate-700 sm:h-8 sm:text-sm"
              >
                {CONDITIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">Notes</span>
            <Textarea className="mt-0.5 resize-none text-sm" rows={2}
              placeholder="Wear, repairs, where it came from…"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </label>

          {pane && pane !== 'add' && (
            <p className="pt-1 text-[11px] text-slate-400">
              Used {pane.times_used || 0} time{pane.times_used === 1 ? '' : 's'}
              {pane.last_used_at && `, last on ${formatIST(pane.last_used_at, { dateOnly: true })}`}
            </p>
          )}
        </div>
      </SidePane>

      <SidePane
        open={!!moving}
        onClose={() => { setMoving(null); setMoveTo(''); }}
        title={`Move ${moving?.code || ''}`}
        subtitle={moving?.location ? `Currently on ${moving.location}` : 'Not placed yet'}
        width="22rem"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => { setMoving(null); setMoveTo(''); }}>Cancel</Button>
            <Button size="sm" onClick={move} className="bg-brand-blue text-white hover:bg-brand-blue-dark">
              Move
            </Button>
          </>
        }
      >
        <div className="p-3">
          <label className="block">
            <span className="text-[11px] font-medium text-slate-600">New location</span>
            <Input className="mt-0.5 h-10 text-base sm:h-8 sm:text-sm" placeholder="Rack or shelf"
              value={moveTo}
              onChange={e => setMoveTo(e.target.value)} />
          </label>
        </div>
      </SidePane>

      <AlertDialog open={!!retiring} onOpenChange={() => setRetiring(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire {retiring?.code}?</AlertDialogTitle>
            <AlertDialogDescription>
              It comes off the list of {tooling.plural} that can be reused. The row stays,
              so it still answers what a past job was run with.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            className="h-10 text-base sm:h-8 sm:text-sm"
            placeholder="Reason — worn out, lost, superseded…"
            aria-label="Reason for retiring"
            value={retireReason}
            onChange={e => setRetireReason(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={retire} className="bg-red-600 text-white hover:bg-red-700">
              Retire
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Screen>
  );
}
